import { Aircraft, AircraftFormData, MaintenanceLog } from '../types/aircraft';
import { supabase } from '../lib/supabaseClient';
import { requirePermission, PermissionContext, fetchPermissionContext } from '../utils/permissionEnforcement';
import { logAircraftEvent } from '../utils/auditLogger';
import { logger } from '../utils/logger';
import { NotificationService } from './notificationService';

const MAINTENANCE_BUCKET = 'maintenance-reports';

export class AircraftService {
  private static formatSupabaseError(action: string, error: any): Error {
    if (!error) return new Error(`Failed to ${action}`);
    const code = typeof error.code === 'string' ? error.code : 'unknown';
    const message = typeof error.message === 'string' ? error.message : String(error);
    const details = typeof error.details === 'string' ? error.details : '';
    const hint = typeof error.hint === 'string' ? error.hint : '';
    const extra = [details && `details: ${details}`, hint && `hint: ${hint}`].filter(Boolean).join(' | ');
    return new Error(extra ? `${message} (code: ${code}) | ${extra}` : `${message} (code: ${code})`);
  }

  static async createAircraft(
    orgId: string, 
    data: AircraftFormData,
    userId: string,
    permissionContext?: PermissionContext
  ): Promise<string> {
    // Fetch permission context if not provided
    const context = permissionContext || await fetchPermissionContext(userId, orgId);
    
    // Enforce permission: AIRCRAFT:CREATE
    requirePermission(context, 'AIRCRAFT:CREATE', 'create aircraft');

    const now = new Date().toISOString();
    const aircraftId = crypto.randomUUID();
    // DB constraints: (1) status='available' => is_grounded=false; (2) status='available' => maintenance_status != 'in_maintenance'
    const status = data.status ?? 'available';
    const isGrounded = status === 'available' ? false : (data.isGrounded ?? false);
    const groundedReason = status === 'available' ? null : (data.groundedReason || null);
    const maintenanceStatus = status === 'available' && data.maintenanceStatus === 'in_maintenance'
      ? 'available'
      : (data.maintenanceStatus || 'available');

    const insertData: any = {
      id: aircraftId,
      org_id: orgId,
      registration: data.registration,
      manufacturer: data.manufacturer,
      model: data.model,
      year: data.year,
      base_airport: data.baseAirportICAO,
      category: data.category,
      avionics: data.avionics,
      fuel_type: data.fuelType,
      hourly_rate: data.hourlyRate,
      dry_rate: data.dryRate !== undefined ? data.dryRate : (data.hourlyRate ?? 0),
      wet_rate: data.wetRate !== undefined ? data.wetRate : (data.hourlyRate ?? 0),
      booking_enabled: data.bookingEnabled,
      is_archived: data.isArchived,
      color: data.color,
      type: data.type,
      status,
      maintenance_status: maintenanceStatus,
      time_source: data.timeSource || 'hobbs',
      current_hours: data.currentHours,
      maintenance_interval_status: 'OK',
      next_inspection_date: data.nextInspectionDate || null,
      is_grounded: isGrounded,
      grounded_reason: groundedReason,
      created_at: now,
      updated_at: now,
    };
    
    const { data: inserted, error } = await supabase
      .from('aircraft')
      .insert(insertData)
      .select('id')
      .single();

    if (error || !inserted) {
      if (error?.code === '42501') {
        logger.error('[RLS_VIOLATION] Aircraft INSERT blocked by RLS policy', {
          table: 'aircraft',
          operation: 'INSERT',
          userId,
          orgId,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
        });
      }
      logger.error('[AircraftService] createAircraft failed', {
        orgId,
        userId,
        supabaseError: error ? { message: error.message, code: error.code, details: error.details } : null,
      });
      throw this.formatSupabaseError('create aircraft', error);
    }

    // Log audit event (non-blocking)
    logAircraftEvent(
      orgId,
      userId,
      context.userRole,
      'CREATE',
      inserted.id,
      `Aircraft ${data.registration} created by ${context.userRole}`
    ).catch(err => logger.error('[AircraftService] Failed to log audit event:', err));

    return inserted.id;
  }

  static async updateAircraft(
    orgId: string, 
    id: string, 
    data: Partial<AircraftFormData>,
    userId: string,
    permissionContext?: PermissionContext
  ): Promise<void> {
    // Fetch permission context if not provided
    const context = permissionContext || await fetchPermissionContext(userId, orgId);
    
    // Enforce permission: AIRCRAFT:UPDATE
    requirePermission(context, 'AIRCRAFT:UPDATE', 'update aircraft');

    const payload: any = {
      updated_at: new Date().toISOString(),
    };

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        switch (key) {
          case 'registration':
          case 'manufacturer':
          case 'model':
          case 'year':
          case 'baseAirportICAO':
          case 'category':
          case 'fuelType':
          case 'timeSource':
          case 'color':
          case 'type':
          case 'status':
          case 'groundedReason':
            payload[this.mapFieldKey(key)] = value;
            break;
          case 'avionics':
            payload[this.mapFieldKey(key)] = value;
            break;
          case 'hourlyRate':
          case 'currentHours':
          case 'dryRate':
          case 'wetRate':
            payload[this.mapFieldKey(key)] = value;
            break;
          case 'bookingEnabled':
          case 'isArchived':
          case 'isGrounded':
            payload[this.mapFieldKey(key)] = value;
            break;
          case 'nextInspectionDate':
            if (value !== '' && value !== null && value !== undefined) {
              payload.next_inspection_date = value;
            } else {
              payload.next_inspection_date = null;
            }
            break;
          case 'maintenanceStatus':
            payload.maintenance_status = value;
            break;
          default:
            // Ignore UI-only fields like maintenanceItems
            if (key === 'maintenanceItems') {
              break;
            }
            payload[key] = value;
            break;
        }
      }
    });

    logger.info('[AircraftService.updateAircraft] Attempting update', {
      orgId,
      id,
      userId,
      payloadKeys: Object.keys(payload),
      payload,
    });

    const { data: updatedRows, error } = await supabase
      .from('aircraft')
      .update(payload)
      .eq('id', id)
      .eq('org_id', orgId)
      .select();

    if (error) {
      if (error.code === '42501') {
        logger.error('[RLS_VIOLATION] Aircraft UPDATE blocked by RLS policy', {
          table: 'aircraft',
          operation: 'UPDATE',
          id,
          userId,
          orgId,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
        });
      }
      logger.error('[AircraftService.updateAircraft] Update error', {
        id,
        orgId,
        supabaseError: { message: error.message, code: error.code, details: error.details },
      });
      throw this.formatSupabaseError('update aircraft', error);
    }

    logger.info('[AircraftService.updateAircraft] Update response', {
      orgId,
      id,
      updatedRowsCount: Array.isArray(updatedRows) ? updatedRows.length : null,
      updatedRows,
    });

    if (!updatedRows || updatedRows.length === 0) {
      throw new Error(`Aircraft with id ${id} not found in organization ${orgId}`);
    }

    // If the hours were corrected here, clear any stale-hours marker (MVP1-P1-11).
    if (data.currentHours !== undefined) {
      await this.clearStaleHoursFlag(orgId, id);
    }

    // Determine action type for audit log
    let actionType: 'UPDATE' | 'GROUND' | 'UNGROUND' = 'UPDATE';
    let description = `Aircraft updated by ${context.userRole}`;
    
    if (data.isGrounded !== undefined) {
      actionType = data.isGrounded ? 'GROUND' : 'UNGROUND';
      description = data.isGrounded 
        ? `Aircraft marked as grounded by ${context.userRole}${data.groundedReason ? `: ${data.groundedReason}` : ''}`
        : `Aircraft marked as ungrounded by ${context.userRole}`;
    }

    // Log audit event (non-blocking)
    logAircraftEvent(
      orgId,
      userId,
      context.userRole,
      actionType,
      id,
      description,
      { changes: Object.keys(payload) }
    ).catch(err => logger.error('[AircraftService] Failed to log audit event:', err));

    // Notify admins + maintenance when aircraft is grounded — OMN-19.
    // Prefer the registration from the updated row so the notification always
    // shows the tail number; the update payload usually omits registration
    // (grounding only sends isGrounded + reason), which previously fell back to
    // the raw aircraft UUID in the alert title.
    if (data.isGrounded === true) {
      const registration = updatedRows[0]?.registration || data.registration || id;
      NotificationService.notifyAircraftGrounded(
        orgId,
        id,
        registration,
        data.groundedReason || 'No reason provided',
        context.userRole
      ).catch(err => logger.error('[AircraftService] Failed to send grounding notification:', err));
    }
  }

  private static mapFieldKey(key: string) {
    switch (key) {
      case 'baseAirportICAO':
        return 'base_airport';
      case 'hourlyRate':
        return 'hourly_rate';
      case 'dryRate':
        return 'dry_rate';
      case 'wetRate':
        return 'wet_rate';
      case 'bookingEnabled':
        return 'booking_enabled';
      case 'isArchived':
        return 'is_archived';
      case 'currentHours':
        return 'current_hours';
      case 'isGrounded':
        return 'is_grounded';
      case 'groundedReason':
        return 'grounded_reason';
      case 'fuelType':
        return 'fuel_type';
      case 'timeSource':
        return 'time_source';
      case 'nextInspectionDate':
        return 'next_inspection_date';
      case 'maintenanceStatus':
        return 'maintenance_status';
      default:
        return key;
    }
  }

  static async updateAircraftHours(
    orgId: string, 
    aircraftId: string, 
    hoursData: {
      currentHours: number;
      maintenanceStatus?: string;
      isGrounded?: boolean;
      groundedReason?: string;
      maintenanceIntervalStatus?: string;
    },
    userId: string,
    permissionContext?: PermissionContext
  ): Promise<void> {
    // Fetch permission context if not provided
    const context = permissionContext || await fetchPermissionContext(userId, orgId);
    
    // Enforce permission: AIRCRAFT:UPDATE_HOURS (less restrictive than full aircraft update)
    requirePermission(context, 'AIRCRAFT:UPDATE_HOURS', 'update aircraft hours');

    const payload: any = {
      current_hours: hoursData.currentHours,
      updated_at: new Date().toISOString(),
    };

    // Update maintenance status if provided
    if (hoursData.maintenanceStatus !== undefined) {
      payload.maintenance_status = hoursData.maintenanceStatus;
    }
    
    // Update interval status if provided
    if (hoursData.maintenanceIntervalStatus !== undefined) {
      payload.maintenance_interval_status = hoursData.maintenanceIntervalStatus;
    }
    
    // Update grounding status if provided
    if (hoursData.isGrounded !== undefined) {
      payload.is_grounded = hoursData.isGrounded;
      if (hoursData.groundedReason !== undefined) {
        payload.grounded_reason = hoursData.groundedReason;
      }
    }

    const { data: updatedRows, error } = await supabase
      .from('aircraft')
      .update(payload)
      .eq('id', aircraftId)
      .eq('org_id', orgId)
      .select();

    if (error) {
      if (error.code === '42501') {
        logger.error('[RLS_VIOLATION] Aircraft hours UPDATE blocked by RLS policy', {
          table: 'aircraft',
          operation: 'UPDATE',
          aircraftId,
          userId,
          orgId,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
        });
      }
      logger.error('[AircraftService.updateAircraftHours] Update error', {
        aircraftId,
        orgId,
        supabaseError: { message: error.message, code: error.code, details: error.details },
      });
      throw error;
    }

    if (!updatedRows || updatedRows.length === 0) {
      throw new Error(`Aircraft with id ${aircraftId} not found in organization ${orgId}`);
    }

    // Log audit event (non-blocking)
    logAircraftEvent(
      orgId,
      userId,
      context.userRole,
      'UPDATE_HOURS',
      aircraftId,
      `Aircraft hours updated to ${hoursData.currentHours} by ${context.userRole}`,
      { newHours: hoursData.currentHours }
    ).catch(err => logger.error('[AircraftService] Failed to log audit event:', err));

    // Hours were written successfully — clear any stale-hours marker (MVP1-P1-11).
    await this.clearStaleHoursFlag(orgId, aircraftId);
  }

  /**
   * Clear the stale-hours marker (MVP1-P1-11) on an aircraft's completed
   * bookings once its hours have been written successfully (manual correction
   * or a later successful update). Best-effort — never throws.
   */
  private static async clearStaleHoursFlag(orgId: string, aircraftId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ hours_update_failed: false })
        .eq('org_id', orgId)
        .eq('aircraft_id', aircraftId)
        .eq('hours_update_failed', true);
      if (error) {
        logger.warn('[AircraftService] Failed to clear stale-hours flag', {
          orgId, aircraftId, error: error.message,
        });
      }
    } catch (err) {
      logger.warn('[AircraftService] Failed to clear stale-hours flag', {
        orgId, aircraftId, error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Aircraft that have a completed booking whose hours update failed
   * (MVP1-P1-11). Powers the admin dashboard "aircraft hours may be out of
   * date" alert. Returns one entry per aircraft (most recent failed flight).
   */
  static async getStaleHoursAircraft(
    orgId: string
  ): Promise<Array<{ aircraftId: string; registration: string; lastFlightEnd: string | null; bookingId: string }>> {
    const { data, error } = await supabase
      .from('bookings')
      .select('id, aircraft_id, end, aircraft:aircraft_id(registration)')
      .eq('org_id', orgId)
      .eq('status', 'completed')
      .eq('hours_update_failed', true)
      .order('end', { ascending: false });

    if (error) {
      logger.error('[AircraftService.getStaleHoursAircraft] query failed', { orgId, error: error.message });
      return [];
    }

    const byAircraft = new Map<string, { aircraftId: string; registration: string; lastFlightEnd: string | null; bookingId: string }>();
    for (const row of (data || []) as any[]) {
      if (!row.aircraft_id || byAircraft.has(row.aircraft_id)) continue;
      const reg = Array.isArray(row.aircraft) ? row.aircraft[0]?.registration : row.aircraft?.registration;
      byAircraft.set(row.aircraft_id, {
        aircraftId: row.aircraft_id,
        registration: reg || row.aircraft_id,
        lastFlightEnd: row.end ?? null,
        bookingId: row.id,
      });
    }
    return Array.from(byAircraft.values());
  }

  static async archiveAircraft(
    orgId: string, 
    id: string,
    userId: string,
    permissionContext?: PermissionContext
  ): Promise<void> {
    // Fetch permission context if not provided
    const context = permissionContext || await fetchPermissionContext(userId, orgId);
    
    // Enforce permission: AIRCRAFT:UPDATE (archiving is an update operation)
    requirePermission(context, 'AIRCRAFT:UPDATE', 'archive aircraft');

    const { error } = await supabase
      .from('aircraft')
      .update({
        is_archived: true,
        booking_enabled: false,
        status: 'archived',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('org_id', orgId);

    if (error) {
      if (error.code === '42501') {
        logger.error('[RLS_VIOLATION] Aircraft UPDATE (archive) blocked by RLS policy', {
          table: 'aircraft',
          operation: 'UPDATE',
          id,
          userId,
          orgId,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
        });
      }
      logger.error('[AircraftService] archiveAircraft failed', {
        id,
        orgId,
        supabaseError: { message: error.message, code: error.code, details: error.details },
      });
      throw error;
    }

    // Log audit event (non-blocking)
    logAircraftEvent(
      orgId,
      userId,
      context.userRole,
      'ARCHIVE',
      id,
      `Aircraft archived by ${context.userRole}`
    ).catch(err => logger.error('[AircraftService] Failed to log audit event:', err));
  }

  private static mapRowToAircraft(row: any): Aircraft {
    return {
      id: row.id,
      orgId: row.org_id,
      registration: row.registration,
      manufacturer: row.manufacturer,
      model: row.model,
      year: row.year,
      baseAirportICAO: row.base_airport || row.base_airport_icao || '',
      category: row.category,
      avionics: row.avionics || [],
      fuelType: row.fuel_type,
      hourlyRate: row.hourly_rate,
      dryRate: row.dry_rate,
      wetRate: row.wet_rate,
      bookingEnabled: row.booking_enabled,
      isArchived: row.is_archived,
      color: row.color,
      type: row.type,
      status: row.status,
      timeSource: row.time_source || 'hobbs',
      maintenanceStatus: row.maintenance_status,
      currentHours: row.current_hours,
      computedTotalHobbs: typeof row.computed_total_hours === 'number' ? row.computed_total_hours : undefined,
      computedTotalTacho: typeof row.computed_total_tacho === 'number' ? row.computed_total_tacho : undefined,
      computedTotalAir: typeof row.computed_total_air === 'number' ? row.computed_total_air : undefined,
      computedTotalBlock: typeof row.computed_total_block === 'number' ? row.computed_total_block : undefined,
      nextInspectionDate: row.next_inspection_date,
      isGrounded: row.is_grounded,
      groundedReason: row.grounded_reason,
      maintenanceIntervalStatus: row.maintenance_interval_status || 'OK',
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  static async getAircraftList(orgId: string, showArchived: boolean = false): Promise<Aircraft[]> {
    const { data, error } = await supabase
      .from('aircraft')
      .select('*')
      .eq('org_id', orgId)
      .order('registration', { ascending: true });

    if (error) {
      throw error;
    }

    const list = (data || []).map(row => this.mapRowToAircraft(row));

    // When showArchived is false (default), filter out archived aircraft so that
    // selection lists (e.g. maintenance reports) only show active aircraft.
    if (!showArchived) {
      return list.filter(item => !item.isArchived && item.status !== 'archived');
    }

    return list;
  }

  /**
   * Read-only aircraft preview for a prospect who is not yet an org member
   * (the join-request flow). Uses a SECURITY DEFINER RPC since normal
   * aircraft RLS requires membership.
   */
  static async getAircraftPreview(orgId: string): Promise<Aircraft[]> {
    const { data, error } = await supabase.rpc('get_aircraft_preview_by_org_id', { p_org_id: orgId });

    if (error) {
      throw error;
    }

    return (data || []).map((row: any) => this.mapRowToAircraft(row));
  }

  static async getAircraft(orgId: string, id: string): Promise<Aircraft | null> {
    const { data, error } = await supabase
      .from('aircraft')
      .select('*')
      .eq('id', id)
      .eq('org_id', orgId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data ? this.mapRowToAircraft(data) : null;
  }

  static async searchAircraft(
    orgId: string, 
    searchTerm: string, 
    showArchived: boolean = false
  ): Promise<Aircraft[]> {
    const list = await this.getAircraftList(orgId, showArchived);
    if (!searchTerm) return list;

    const queryLower = searchTerm.toLowerCase();
    return list.filter(item =>
      item.registration.toLowerCase().includes(queryLower) ||
      item.model.toLowerCase().includes(queryLower) ||
      item.manufacturer.toLowerCase().includes(queryLower)
    );
  }

  static async addMaintenanceLog(
    orgId: string,
    aircraftId: string,
    log: Omit<MaintenanceLog, 'id'>
  ): Promise<string> {
    const insertData = {
      org_id: orgId,
      aircraft_id: aircraftId,
      date_performed: log.datePerformed?.split('T')[0] ?? log.datePerformed,
      hours_at_service: log.hoursAtService,
      type_of_maintenance: log.typeOfMaintenance,
      notes: log.notes || '',
      files: log.files || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('maintenance_logs')
      .insert(insertData)
      .select('id')
      .single();

    if (error || !data) {
      logger.error('Failed to add maintenance log:', error);
      throw error || new Error('Failed to add maintenance log');
    }

    return data.id;
  }

  static async getMaintenanceLogs(
    orgId: string,
    aircraftId: string
  ): Promise<MaintenanceLog[]> {
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select('*')
      .eq('org_id', orgId)
      .eq('aircraft_id', aircraftId)
      .order('date_performed', { ascending: false });

    if (error) {
      throw error;
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      datePerformed: row.date_performed,
      hoursAtService: row.hours_at_service ?? 0,
      typeOfMaintenance: row.type_of_maintenance ?? '',
      notes: row.notes ?? '',
      files: row.files ?? [],
    })) as MaintenanceLog[];
  }

  static async updateMaintenanceLogFiles(
    orgId: string,
    aircraftId: string,
    logId: string,
    filesArr: { fileName: string; url: string }[]
  ): Promise<void> {
    const { error } = await supabase
      .from('maintenance_logs')
      .update({
        files: filesArr,
        updated_at: new Date().toISOString(),
      })
      .eq('id', logId)
      .eq('org_id', orgId)
      .eq('aircraft_id', aircraftId);

    if (error) {
      throw error;
    }
  }
} 

// Upload files to Firebase Storage for a maintenance log
export async function uploadMaintenanceFiles(
  orgId: string,
  aircraftId: string,
  logId: string,
  files: FileList | File[]
): Promise<{ fileName: string; url: string }[]> {
  const uploadResults: { fileName: string; url: string }[] = [];
  for (const file of Array.from(files)) {
    const path = `organizations/${orgId}/aircraft/${aircraftId}/logs/${logId}/${file.name}`;
    const { error } = await supabase.storage.from(MAINTENANCE_BUCKET).upload(path, file, {
      upsert: true,
    });
    if (error) {
      throw error;
    }
    const { data: publicUrl } = supabase.storage.from(MAINTENANCE_BUCKET).getPublicUrl(path);
    uploadResults.push({ fileName: file.name, url: publicUrl.publicUrl });
  }
  return uploadResults;
} 

// Delete a file from Firebase Storage for a maintenance log
export async function deleteMaintenanceFile(
  orgId: string,
  aircraftId: string,
  logId: string,
  fileName: string
): Promise<void> {
  const path = `organizations/${orgId}/aircraft/${aircraftId}/logs/${logId}/${fileName}`;
  const { error } = await supabase.storage.from(MAINTENANCE_BUCKET).remove([path]);
  if (error) {
    throw error;
  }
}

// Remove a file entry from the files array in a maintenance log
export async function removeFileFromMaintenanceLog(
  orgId: string,
  aircraftId: string,
  logId: string,
  fileObj: { fileName: string; url: string }
): Promise<void> {
  const { data } = await supabase
    .from('maintenance_logs')
    .select('files')
    .eq('id', logId)
    .eq('org_id', orgId)
    .eq('aircraft_id', aircraftId)
    .single();

  const files = (data?.files || []).filter((file: any) => file.fileName !== fileObj.fileName);

  await supabase
    .from('maintenance_logs')
    .update({
      files,
      updated_at: new Date().toISOString(),
    })
    .eq('id', logId)
    .eq('org_id', orgId)
    .eq('aircraft_id', aircraftId);
} 